import { GlobalOutlined, LockOutlined, UserAddOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Alert, Card, Popover, Typography, Space, Form, Flex, Button, Input } from "antd";
import { LangDropdown } from "components/layout/header/languageDropdown";
import { useUpdatePassword } from "@refinedev/core";
import type { UpdatePasswordFormTypes } from "@refinedev/core/src/interfaces/auth";
import { useState } from "react";
import { InviteUserDialog } from "pages/users/inviteUserDialog";
import { getUserInfoFromCache, isAdminOrSuperAdmin } from "utility/helper";

const { Text, Title } = Typography;

export interface IUpdatePasswordForm {
  oldPassword: string;
  newPassword: string;
}

export interface IUpdatePasswordResponse {
  success: boolean;
  responseInfo?: {
    name?: string;
    message?: string;
  };
}

export const PersonalSettings: React.FC = () => {
  const [form] = Form.useForm<IUpdatePasswordForm>();
  const [updatePasswordAlert, setUpdatePasswordAlert] = useState<JSX.Element | undefined | null>(
    <></>
  );
  const [inviteOpen, setInviteOpen] = useState(false);
  const { mutate: updatePassword, isLoading } = useUpdatePassword<UpdatePasswordFormTypes>();
  const canInvite = isAdminOrSuperAdmin();
  const currentTenantId = Number(getUserInfoFromCache()?.tenant);

  const handleUpdatePassword = (oldPassword: string, newPassword: string) => {
    updatePassword(
      { password: oldPassword, confirmPassword: newPassword },
      {
        onSuccess: (data: IUpdatePasswordResponse) => {
          if (!data.success) {
            setUpdatePasswordAlert(
              <Alert
                style={{ marginTop: "1rem" }}
                message={data?.responseInfo?.name ?? "Error"}
                type="error"
                description={data?.responseInfo?.message ?? "Failed to update password."}
                showIcon
                closable
                onClose={() => setUpdatePasswordAlert(<></>)}
              />
            );
          } else {
            setUpdatePasswordAlert(
              <Alert
                style={{ marginTop: "1rem" }}
                message={data?.responseInfo?.name ?? "Success"}
                type="success"
                description={data?.responseInfo?.message ?? "Failed to update password."}
                showIcon
                closable
                onClose={() => setUpdatePasswordAlert(<></>)}
              />
            );
          }
        },
      }
    );
  };

  const newPasswordContentInfo = (
    <Text>
      Ditt lösenord måste innehålla minst 8 tecken, inkludera både stora och små bokstäver, ett
      nummer och ett specialtecken (exempel: @, #, $)
    </Text>
  );

  return (
    <Space direction="vertical" size="large" style={{ display: "flex" }}>
      <Card bordered={false} className="criclebox h-full">
        <Space direction="vertical" size="middle" style={{ display: "flex" }}>
          <Flex wrap="wrap" gap="large" className="align-center">
            <GlobalOutlined style={{ fontSize: "30px", color: "#626262" }} />
            <div>
              <Title level={4} style={{ marginBottom: "0px" }}>
                Språk
              </Title>
              <Text style={{ color: "#626262" }}>Gör ditt språkval</Text>
            </div>
          </Flex>
          <LangDropdown />
        </Space>
      </Card>

      <Card bordered={false} className="criclebox h-full">
        <Space direction="vertical" size="large" style={{ display: "flex" }}>
          <Flex wrap="wrap" gap="large" className="align-center">
            <LockOutlined style={{ fontSize: "30px", color: "#626262" }} />
            <div>
              <Title level={4} style={{ marginBottom: "0px" }}>
                Lösenord
              </Title>
              <Text style={{ color: "#626262" }}>Ändra lösenord </Text>
            </div>
          </Flex>
          <Form<IUpdatePasswordForm>
            layout="vertical"
            form={form}
            onFinish={(values) => {
              handleUpdatePassword(values.oldPassword, values.newPassword);
            }}
            requiredMark={true}
            initialValues={{ remember: false }}
          >
            <Form.Item name="oldPassword" label="Nuvarande lösenord" rules={[{ required: true }]}>
              <Input type="password" placeholder="●●●●●●●●" size="large" />
            </Form.Item>
            <Form.Item
              name="newPassword"
              label={
                <>
                  <Text>Nytt lösenord</Text>
                  <Popover content={newPasswordContentInfo}>
                    <ExclamationCircleOutlined style={{ marginLeft: "0.5rem" }} />
                  </Popover>
                </>
              }
              rules={[{ required: true }]}
            >
              <Input type="password" placeholder="●●●●●●●●" size="large" />
            </Form.Item>
            <Button type="primary" htmlType="submit" disabled={isLoading} loading={isLoading}>
              Ändra lösenord
            </Button>
          </Form>
        </Space>
        {updatePasswordAlert}
      </Card>

      {canInvite && (
        <Card bordered={false} className="criclebox h-full">
          <Space direction="vertical" size="middle" style={{ display: "flex" }}>
            <Flex wrap="wrap" gap="large" className="align-center">
              <UserAddOutlined style={{ fontSize: "30px", color: "#626262" }} />
              <div>
                <Title level={4} style={{ marginBottom: "0px" }}>
                  Bjud in användare
                </Title>
                <Text style={{ color: "#626262" }}>Skicka en inbjudan till en ny användare</Text>
              </div>
            </Flex>
            <Button type="primary" onClick={() => setInviteOpen(true)}>
              Bjud in
            </Button>
          </Space>
          <InviteUserDialog
            open={inviteOpen}
            onClose={() => setInviteOpen(false)}
            onSuccess={() => undefined}
            defaultTenantId={Number.isNaN(currentTenantId) ? null : currentTenantId}
          />
        </Card>
      )}
    </Space>
  );
};
